import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { api, type Item } from '../api.js';
import { Button, Card, Empty, ErrorBox, Loading, Screen, StateBadge, categoryLabel} from '../ui.js';

const FILTERS = [
  { key: 'all', label: 'Alle' },
  { key: 'available', label: 'Verfügbar' },
  { key: 'laundry', label: 'In der Wäsche' },
  { key: 'care', label: 'Reinigung fällig' },
];

export function Wardrobe({ navigate }: { navigate: (to: string) => void }): ReactNode {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const items = useQuery({ queryKey: ['items'], queryFn: () => api.items() });

  const visible = useMemo(() => {
    const list = items.data ?? [];
    const term = search.trim().toLowerCase();
    return list.filter((i) => {
      // Gesucht wird ueber das, was auf dem Bildschirm steht: wer „Hemd" tippt,
      // soll Hemden finden — nicht nur, wer „shirt" tippt.
      const heuhaufen = `${i.name} ${i.brand ?? ''} ${categoryLabel(i.category)} ${i.category}`;
      if (term !== '' && !heuhaufen.toLowerCase().includes(term)) {
        return false;
      }
      if (filter === 'available') return i.availability.available;
      if (filter === 'laundry') {
        return ['in_hamper', 'washing', 'drying', 'ironing', 'soiled'].includes(i.lifecycleState);
      }
      if (filter === 'care') return i.needsCleaning || i.lifecycleState === 'at_care';
      return true;
    });
  }, [items.data, filter, search]);

  return (
    <Screen
      title="Schrank"
      action={<Button onClick={() => navigate('/schrank/neu')}>+ Neu</Button>}
    >
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Suchen …"
        className="mb-3 w-full rounded-xl bg-white/6 px-4 py-3 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
      />

      <div className="-mx-4 mb-4 overflow-x-auto px-4">
        <div className="flex gap-2 pb-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 rounded-full px-4 py-2 text-sm ${
                filter === f.key ? 'bg-white text-black' : 'bg-white/8 text-white/75'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {items.isLoading && <Loading />}
      <ErrorBox error={items.error} />

      {items.data !== undefined && visible.length === 0 && (
        <Empty>
          {items.data.length === 0
            ? 'Noch nichts erfasst. Fang mit ein paar Teilen an, die du täglich trägst.'
            : 'Nichts gefunden.'}
        </Empty>
      )}

      <div className="space-y-2">
        {visible.map((item) => (
          <ItemRow key={item.id} item={item} onOpen={() => navigate(`/schrank/${item.id}`)} />
        ))}
      </div>

      {items.data !== undefined && items.data.length > 0 && (
        <p className="py-6 text-center text-xs text-white/30">
          {visible.length} von {items.data.length} Teilen
        </p>
      )}
    </Screen>
  );
}

function ItemRow({ item, onOpen }: { item: Item; onOpen: () => void }): ReactNode {
  return (
    <button
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-[var(--radius-card)] bg-panel p-3 text-left ring-1 ring-white/5"
    >
      <div
        className="size-12 shrink-0 rounded-xl ring-1 ring-white/10"
        style={{ background: item.colorPrimaryHex ?? '#334155' }}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{item.name}</p>
        <p className="truncate text-xs text-white/55">
          {item.brand !== null && `${item.brand} · `}
          {categoryLabel(item.category)}
          {item.totalWears > 0 && ` · ${item.totalWears}× getragen`}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <StateBadge state={item.lifecycleState} />
        {!item.availability.available && item.lifecycleState === 'worn_ok' && (
          <span className="text-[10px] text-white/45">ruht</span>
        )}
        {item.needsCleaning && (
          <span className="text-[10px] text-purple-300">Reinigung fällig</span>
        )}
      </div>
    </button>
  );
}

export { Card };
